
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import { isAuthenticated } from '../utils/auth';
import api from '../services/api';
import './Transcriptions.css';

interface CallData {
  id: string;
  call_sid: string;
  direction: string;
  from_number: string;
  to_number: string;
  call_status: string;
  created_at: string;
  conversations: {
    id: string;
    from_number: string;
    to_number: string;
  };
}

interface Transcription {
  id: string;
  call_id: string;
  recording_sid: string;
  recording_url: string;
  recording_duration: number;
  transcription_text: string;
  transcription_status: string;
  created_at: string;
  calls: CallData;
}

const Transcriptions: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Transcriptions');
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  // Audio player modal state
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [currentRecording, setCurrentRecording] = useState<{
    id: string;
    recordingUrl: string;
    fromNumber: string;
    toNumber: string;
  } | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchTranscriptions();
  }, [navigate]);

  useEffect(() => {
    // When modal opens, load the audio (but don't autoplay)
    if (showAudioModal && currentRecording && audioRef.current) {
      const audio = audioRef.current;
      audio.load();
      
      // Add error handler for audio
      audio.onerror = (e) => {
        console.error('Audio error:', e);
      };
      
      audio.oncanplay = () => {
        console.log('Audio can play now');
      };
    }
  }, [showAudioModal, currentRecording]);

  const fetchTranscriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/transcriptions');
      console.log('Transcriptions API Response:', response.data);
      
      if (response.data.success) {
        setTranscriptions(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load transcriptions');
      }
    } catch (err: any) {
      console.error('Error fetching transcriptions:', err);
      setError(err.response?.data?.message || 'Failed to load transcriptions data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`/${tab.toLowerCase()}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusColor = (status: string) => {
    switch(status?.toLowerCase()) {
      case 'completed': return '#2ecc71';
      case 'in-progress': return '#f39c12';
      case 'failed': return '#e74c3c';
      case 'pending': return '#95a5a6';
      default: return '#95a5a6';
    }
  };

  const getDirectionIcon = (direction: string) => {
    return direction === 'inbound' ? '📞 Inbound' : '📤 Outbound';
  };

  // Open audio player modal
  const playRecording = (recordingSid: string, fromNumber: string, toNumber: string) => {
    const streamUrl = `${api.defaults.baseURL}/admin/stream/${recordingSid}`;
    
    console.log('Playing recording URL:', streamUrl);
    console.log('Recording SID:', recordingSid);
    
    setCurrentRecording({
      id: recordingSid,
      recordingUrl: streamUrl,
      fromNumber: fromNumber,
      toNumber: toNumber
    });
    
    setShowAudioModal(true);
  };

  // Close audio modal
  const closeAudioModal = () => {
    setShowAudioModal(false);
    setCurrentRecording(null);
  };

  if (!isAuthenticated()) {
    return null;
  }

  if (loading) {
    return (
      <div className="transcriptions-page">
        <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="page-content">
          <div className="loading-container">Loading transcriptions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="transcriptions-page">
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="page-content">
        <h1>Transcriptions</h1>
        {error && <div className="error-message">{error}</div>}
        
        {transcriptions.length === 0 && !error ? (
          <div className="no-data">No transcriptions found</div>
        ) : (
          <div className="transcriptions-list">
            {transcriptions.map((transcription) => (
              <div key={transcription.id} className="transcription-card">
                <div className="transcription-header" onClick={() => toggleExpand(transcription.id)}>
                  <div className="transcription-info">
                    <div className="call-info">
                      <span className="call-sid">{transcription.calls?.call_sid?.substring(0, 15)}...</span>
                      <span className="direction-badge">{getDirectionIcon(transcription.calls?.direction)}</span>
                    </div>
                    <div className="participants">
                      <span className="from-number">{transcription.calls?.from_number}</span>
                      <span className="arrow">→</span>
                      <span className="to-number">{transcription.calls?.to_number}</span>
                    </div>
                    <div className="transcription-meta">
                      <span className="date">{formatDate(transcription.created_at)}</span>
                      <span className="duration">⏱️ {transcription.recording_duration} sec</span>
                      <span 
                        className="status-badge" 
                        style={{ background: getStatusColor(transcription.transcription_status) }}
                      >
                        {transcription.transcription_status}
                      </span>
                    </div>
                  </div>
                  <div className="expand-icon">
                    {expandedId === transcription.id ? '▲' : '▼'}
                  </div>
                </div>
                
                {expandedId === transcription.id && (
                  <div className="transcription-details">
                    <div className="details-section">
                      <h4>Call Details</h4>
                      <div className="detail-row">
                        <strong>Call SID:</strong> {transcription.calls?.call_sid}
                      </div>
                      <div className="detail-row">
                        <strong>From:</strong> {transcription.calls?.from_number}
                      </div>
                      <div className="detail-row">
                        <strong>To:</strong> {transcription.calls?.to_number}
                      </div>
                      <div className="detail-row">
                        <strong>Direction:</strong> {transcription.calls?.direction}
                      </div>
                      <div className="detail-row">
                        <strong>Call Status:</strong> 
                        <span className="call-status" style={{ background: getStatusColor(transcription.calls?.call_status), marginLeft: '8px', padding: '2px 8px', borderRadius: '12px', color: 'white', fontSize: '0.8rem' }}>
                          {transcription.calls?.call_status}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Call Time:</strong> {formatDate(transcription.calls?.created_at)}
                      </div>
                    </div>
                    
                    <div className="transcription-section">
                      <h4>Transcription Text</h4>
                      <div className="transcription-text-full">
                        {transcription.transcription_text}
                      </div>
                    </div>
                    
                    <div className="recording-section">
                      <h4>Recording Details</h4>
                      <div className="detail-row">
                        <strong>Recording SID:</strong> {transcription.recording_sid}
                      </div>
                      <div className="detail-row">
                        <strong>Duration:</strong> {transcription.recording_duration} seconds
                      </div>
                      {transcription.recording_sid && (
                        <div className="detail-row">
                          <strong>Recording:</strong>
                          <button 
                            onClick={() => playRecording(transcription.recording_sid, transcription.calls?.from_number, transcription.calls?.to_number)}
                            className="play-button"
                          >
                            🎧 Play Recording
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="conversation-section">
                      <h4>Conversation Info</h4>
                      <div className="detail-row">
                        <strong>Conversation ID:</strong> {transcription.calls?.conversations?.id}
                      </div>
                      <div className="detail-row">
                        <strong>Participants:</strong> {transcription.calls?.conversations?.from_number} ↔ {transcription.calls?.conversations?.to_number}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audio Player Modal */}
      {showAudioModal && currentRecording && (
        <div className="audio-modal-overlay" onClick={closeAudioModal}>
          <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
            <div className="audio-modal-header">
              <h3>🎙️ Call Recording Player</h3>
              <button className="close-button" onClick={closeAudioModal}>✕</button>
            </div>
            <div className="audio-modal-body">
              <div className="call-info-modal">
                <div className="info-row">
                  <span className="label">📞 From:</span>
                  <span className="value">{currentRecording.fromNumber}</span>
                </div>
                <div className="info-row">
                  <span className="label">🎯 To:</span>
                  <span className="value">{currentRecording.toNumber}</span>
                </div>
              </div>
              <div className="audio-player-container">
                <audio 
                  ref={audioRef}
                  controls 
                  className="audio-player"
                  preload="auto"
                  crossOrigin="anonymous"
                >
                  <source src={currentRecording.recordingUrl} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </div>
              <div className="audio-actions">
                <a 
                  href={currentRecording.recordingUrl} 
                  download 
                  className="download-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  💾 Download Recording
                </a>
              </div>
            </div>
            <div className="audio-modal-footer">
              <button className="close-modal-btn" onClick={closeAudioModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transcriptions;